import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground',
        outline: 'text-foreground',
        success: 'border-transparent bg-success text-success-foreground',
        warning: 'border-transparent bg-warning text-warning-foreground',
        info: 'border-transparent bg-info text-info-foreground',
        muted: 'border-transparent bg-muted text-muted-foreground',
        // The design's status chip: a grey lozenge whose dot carries the
        // meaning. Rendered by passing `dot`, not by naming this variant.
        chip: 'gap-1.5 rounded-md border-transparent bg-muted font-medium text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

/**
 * What a status dot means. Grey: the pipeline owns it. Blue: work in progress.
 * Green: done. Amber / red: a human must act — the one place hue still says
 * "look here".
 */
export type DotTone = 'pipeline' | 'progress' | 'done' | 'attention' | 'danger'

const DOT: Record<DotTone, string> = {
  pipeline: 'bg-muted-foreground/60',
  progress: 'bg-primary',
  done: 'bg-success',
  attention: 'bg-warning',
  danger: 'bg-destructive',
}

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /** Render as a status chip with this dot. Overrides `variant`. */
  dot?: DotTone
}

export function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant: dot ? 'chip' : variant }), className)}
      data-tone={dot}
      {...props}
    >
      {dot && <span className={cn('h-2 w-2 shrink-0 rounded-full', DOT[dot])} aria-hidden="true" />}
      {children}
    </div>
  )
}

export { badgeVariants }
